// FILE: src/components/QuestionEditor.js
//
// In-memory question editor with Supabase-backed persistence.
// All saves are awaited and the UI reflects the actual persisted state.
//
// ── Data model ────────────────────────────────────────────────────────────────
//
//   QuestionEditor owns:
//     questionStores  { pmp: Question[] | null, cpmai: Question[] | null }
//                     Per-exam in-memory stores.  Null means "not yet loaded".
//                     Populated lazily via useEffect when the user switches to
//                     an exam tab for the first time.  Updates (saves) are
//                     written back into this store after Supabase confirms the
//                     write, so the sidebar always reflects the persisted state.
//
//     selectedExam    'pmp' | 'cpmai'  — which exam tab is active
//     searchTerm      string           — substring filter applied to question text
//     selectedId      string | null    — id of the question currently open in
//                                        the editor form
//     formData        object | null    — working copy of the selected question's
//                                        editable fields (detached from the store)
//     errors          { field: string } — validation errors; cleared on save
//     savedId         string | null    — id of the most-recently saved question
//                                        (drives the "saved" confirmation banner)
//     saveStatus      'idle'|'saving'|'saved'|'error'
//     saveError       string | null    — error message when saveStatus === 'error'
//     showHistory     boolean          — whether the history panel is expanded
//     questionHistory HistoryEntry[] | null — null = not fetched yet
//     confirmRetire   boolean          — whether the retire confirmation is shown
//
// ── Normalized question shape ─────────────────────────────────────────────────
//
//   {
//     id            string      unique question identifier
//     type          string      'multiple_choice' | 'multiple_select'
//     question      string      the question stem
//     choices       { id, text }[]   answer choices; id is a single letter (A-Z)
//     correctAnswers  string[]  ids of correct choice(s)
//     explanation   string      rationale shown after answer
//     category      string      topic area / knowledge area
//     source        string      reference or source document (local only, not in DB)
//     confidence    'low'|'medium'|'high'
//     multiSelect   boolean     true if the question allows multiple selections
//     versionNo     number      incremented on every Supabase write (may be undefined
//                               for questions loaded from bundled JSON)
//     updatedAt     string      ISO timestamp of last Supabase write
//     status        'active'|'retired'
//   }
//
// ── Validation rules ──────────────────────────────────────────────────────────
//
//   question        — must be non-empty after trim
//   choices         — must have ≥ 1 entry; each choice text must be non-empty
//   correctAnswers  — must have ≥ 1 entry that exists in choices
//
// ── Save strategy ─────────────────────────────────────────────────────────────
//
//   handleSave() and handleSaveNew() both:
//     1. Validate formData locally
//     2. Set saveStatus = 'saving' (disables the button)
//     3. Await the Supabase RPC (updateQuestionWithHistory / createQuestionWithHistory)
//     4. On success: update local store from the DB response, set saveStatus = 'saved'
//     5. On failure: set saveStatus = 'error' and display the error message
//
//   The local store is NEVER updated before Supabase confirms the write.
//   The formData is refreshed from the DB-returned row so the UI always reflects
//   the persisted state (correct version number, timestamp, etc.).

import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import {
  generateReferenceId,
  normalizeSupabaseQuestion,
  updateQuestionWithHistory,
  createQuestionWithHistory,
  retireQuestion,
  restoreQuestionVersion,
  fetchQuestionHistory,
  fetchMaxQuestionIdNumber,
} from '../../../services/questionService';
import {
  uploadQuestionImage,
  UploadValidationError,
  ALLOWED_MIME_TYPES as IMAGE_UPLOAD_MIME_TYPES,
  MAX_BYTES as IMAGE_UPLOAD_MAX_BYTES,
} from '../../../services/imageUploadService';
import QuestionPreviewModal, { buildPreviewUrl } from '../QuestionPreviewModal';
import { AdminGateContext } from '../../admin/AdminGate';
import {
  EXAM_CATEGORIES, isKnownCategory,
  PMP_PERFORMANCE_DOMAINS, PMP_DELIVERY_CONTEXTS,
  PMI_ACP_PRACTICE_AREAS, COGNITIVE_LEVELS, DIFFICULTIES, EXAM_WEIGHTS,
  SECONDARY_DIMENSION_VALUES, CPMAI_ECO_DOMAINS, isEcoDomainRequired, validateEcoDomain } from '../../../config/examCategories';
import { checkAssertedKey, checkReorderAgainstReferences } from '../../../utils/letterReferences';
import { getAllExams, getActiveCertIds } from '../../../config/exams';
import { ensureCertLoaded } from '../../../services/certBankLoader';
import InteractiveAnswersEditor, {
  INTERACTIVE_TYPES, interactiveFormState, interactivePayload, validateInteractive,
  interactiveTypeLabel,
} from './InteractiveAnswers';
import {
  QUESTION_STATUS_LABELS, QUESTION_STATUS_COLORS,
  REVIEW_STATUSES, REVIEW_STATUS_LABELS, REVIEW_STATUS_COLORS,
} from '../../../config/lifecycleConstants';
import { updateQuestionLifecycle } from '../../../services/questionService';

// ── Pure helpers ──────────────────────────────────────────────────────────────

/**
 * Validates the editor form data.
 * Returns an empty object when valid; otherwise returns { field: message }.
 *
 * @param {object} formData
 * @returns {{ question?: string, choices?: string, correctAnswers?: string }}
 */
export function validateForm(formData, examId = null, original = null) {
  // Interactive (case) types validate through their own sections + the
  // canonical registry spec; the classic checks below don't apply.
  if (INTERACTIVE_TYPES.has(formData.type)) {
    const interactiveErrors = validateInteractive(formData);
    if (!formData.question || !formData.question.trim()) {
      interactiveErrors.question = 'Question text is required.';
    }
    return interactiveErrors;
  }
  const errors = {};

  // ECO domain is a publish precondition for certs that require one (CPMAI).
  // Every other certification is exempt — PMI-ACP keeps its own eco_domain
  // vocabulary and is deliberately not in the required list.
  if (examId && isEcoDomainRequired(examId)) {
    const { ok, error } = validateEcoDomain(examId, formData.ecoDomain);
    if (!ok) errors.ecoDomain = error;
  }

  if (!formData.question || !formData.question.trim()) {
    errors.question = 'Question text is required.';
  }

  if (!formData.choices || formData.choices.length < 1) {
    errors.choices = 'At least one answer choice is required.';
  } else {
    const hasEmpty = formData.choices.some((c) => !c.text || !c.text.trim());
    if (hasEmpty) {
      errors.choices = 'All answer choices must have text.';
    }
  }

  if (!formData.correctAnswers || formData.correctAnswers.length < 1) {
    errors.correctAnswers = 'At least one correct answer must be selected.';
  }

  // ── Letter-reference consistency (publication-quality guard) ────────────
  // Rule 1: if the explanation names a letter as THE correct answer, it must
  // be the key. Rule 2 (only when editing an existing question, passed as
  // `original`): a pure reorder of the choices must not leave the unchanged
  // prose pointing at moved letters. Mirrors the DB trigger from migration
  // 103, so the author gets the friendly message before the RPC refuses.
  if (!errors.correctAnswers && !errors.choices) {
    const assertedErr = checkAssertedKey(formData);
    if (assertedErr) errors.explanation = assertedErr;
    else if (original) {
      const reorderErr = checkReorderAgainstReferences(original, formData);
      if (reorderErr) errors.explanation = reorderErr;
    }
  }

  // ── Phase 3 — visual_interpretation requires image.src ──────────────────
  if (formData.type === 'visual_interpretation') {
    const src = formData.image?.src;
    if (!src || !String(src).trim()) {
      errors.imageSrc = 'Image URL or path is required for visual interpretation questions.';
    }
  }

  return errors;
}

/**
 * Returns soft warnings (non-blocking).  Right now only one: visual exhibits
 * without alt text are accepted but flagged for accessibility.
 *
 * @param {object} formData
 * @returns {{ imageAlt?: string }}
 */
export function getFormWarnings(formData) {
  const warnings = {};
  if (formData.type === 'visual_interpretation') {
    const alt = formData.image?.alt;
    if (!alt || !String(alt).trim()) {
      warnings.imageAlt = 'Alt text is recommended for accessibility (screen readers).';
    }
  }
  return warnings;
}

/**
 * Returns true if the editor form has unsaved edits compared to the canonical
 * stored question.  Used to decide whether Approve needs to save content
 * before flipping the lifecycle state.
 *
 * Compares only the fields that `updateQuestionWithHistory` persists.  The
 * `changeNote` field, `source` (local-only), and any lifecycle metadata are
 * intentionally ignored.
 *
 * Returns false when either argument is null/undefined.
 *
 * @param {object|null} formData  - the editor's working copy
 * @param {object|null} existing  - the canonical question from the store
 * @returns {boolean}
 */
export function isFormDirty(formData, existing) {
  if (!formData || !existing) return false;

  // Canonical persisted shape for either representation: the form's
  // matching/ordering slots or a stored question's object choices. Keeps the
  // comparator honest for the interactive types (where correctAnswers can be
  // ORDER-sensitive and choices aren't an array).
  const interactiveCanon = (q) => {
    if (q.matching) {
      return {
        choices: { prompts: q.matching.prompts.map((x) => ({ id: x.id, text: x.text ?? '' })),
                   options: q.matching.options.map((x) => ({ id: x.id, text: x.text ?? '' })) },
        key: Object.entries(q.matching.pairs).map(([pp, oo]) => `${pp}:${oo}`).sort(),
      };
    }
    if (q.ordering) {
      return {
        choices: { items: q.ordering.items.map((x) => ({ id: x.id, text: x.text ?? '' })) },
        key: [...q.ordering.sequence], // order-sensitive — do NOT sort
      };
    }
    if (q.choices && !Array.isArray(q.choices)) {
      const sortKey = q.type !== 'ordered_sequence';
      const key = [...(q.correctAnswers ?? [])].map(String);
      return { choices: JSON.parse(JSON.stringify(q.choices)), key: sortKey ? key.sort() : key };
    }
    return null;
  };

  const normalize = (q) => ({
    question:          q.question ?? '',
    interactive:       interactiveCanon(q),
    choices:           (Array.isArray(q.choices) ? q.choices : []).map((c) => ({ id: c.id, text: c.text ?? '' })),
    // Sort correctAnswers so order-only differences aren't flagged as dirty.
    correctAnswers:    interactiveCanon(q) ? [] : [...(q.correctAnswers ?? [])].slice().sort(),
    explanation:       q.explanation ?? '',
    category:          q.category ?? '',
    source:            q.source ?? '',
    confidence:        q.confidence ?? 'medium',
    multiSelect:       q.multiSelect ?? false,
    performanceDomain: q.performanceDomain ?? '',
    deliveryContext:   q.deliveryContext   ?? '',
    isExperimental:    q.isExperimental === true,
    // PMI-ACP ECO metadata (migrations 032 + 033). All optional; empty string == not set.
    ecoTask:           q.ecoTask        ?? '',
    practiceArea:      q.practiceArea   ?? '',
    focusArea:         q.focusArea ?? '',
    ecoDomain:         q.ecoDomain ?? '',
    knowledgeArea:     q.knowledgeArea  ?? '',
    artifact:          q.artifact       ?? '',
    cognitiveLevel:    q.cognitiveLevel ?? '',
    difficulty:        q.difficulty     ?? '',
    scenarioBased:     q.scenarioBased === true,
    examWeight:        q.examWeight     ?? '',
    // Phase 3 -- visual_interpretation support
    type:              q.type ?? (q.multiSelect ? 'multiple_select' : 'multiple_choice'),
    image:             {
      src: q.image?.src ?? '',
      alt: q.image?.alt ?? '',
    },
  });

  return JSON.stringify(normalize(formData)) !== JSON.stringify(normalize(existing));
}

/**
 * Appends a new blank choice to the choices array.
 * Choice ids follow A, B, C … Z ordering.
 *
 * @param {Array<{id: string, text: string}>} choices
 * @returns {Array<{id: string, text: string}>}
 */
export function addChoice(choices) {
  const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const id = choices.length < LETTERS.length
    ? LETTERS[choices.length]
    : `opt_${choices.length}`;
  return [...choices, { id, text: '' }];
}

/**
 * Removes the choice at `index` from the array.
 * Also removes the removed choice's id from correctAnswers.
 *
 * @param {Array<{id: string, text: string}>} choices
 * @param {number} index
 * @param {string[]} correctAnswers
 * @returns {{ choices: Array, correctAnswers: string[] }}
 */
export function removeChoice(choices, index, correctAnswers) {
  const removed = choices[index];
  const newChoices = choices.filter((_, i) => i !== index);
  const newCorrect = correctAnswers.filter((id) => id !== removed.id);
  return { choices: newChoices, correctAnswers: newCorrect };
}

/**
 * Per-exam slug used as the prefix for newly generated question IDs.  Kept
 * separate from the examId itself so multi-segment IDs (e.g. "pmi-acp") map
 * to a single-segment slug (e.g. "pmiacp-") that round-trips cleanly through
 * the EMBEDDED_PREFIX_RE strip in generateReferenceId().
 */
const NEW_ID_PREFIX = {
  pmp:       'pmp-',
  cpmai:     'cpmai-',
  'pmi-acp': 'pmiacp-',
};

/**
 * Generates a new unique question ID for the given exam that is one higher than
 * the current maximum numeric suffix in the pool.
 *
 * @param {string}   examId    - "pmp" | "cpmai"
 * @param {object[]} questions - current normalized question pool for this exam
 * @returns {string}           - e.g. "pmp-1027" or "cpmai-0089"
 */
export function generateNewQuestionId(examId, questions) {
  // Lookup the slug; fall back to `${examId}-` for any future exam that
  // forgot to register here so the editor still produces *some* unique ID.
  const prefix = NEW_ID_PREFIX[examId] || `${examId}-`;
  let maxNum = 0;
  for (const q of questions) {
    const raw = String(q.id);
    if (raw.startsWith(prefix)) {
      const num = parseInt(raw.slice(prefix.length), 10);
      if (!isNaN(num) && num > maxNum) maxNum = num;
    }
  }
  return `${prefix}${String(maxNum + 1).padStart(4, '0')}`;
}

// ── Blank form defaults ───────────────────────────────────────────────────────

const BLANK_FORM = {
  question:       '',
  choices:        [
    { id: 'A', text: '' },
    { id: 'B', text: '' },
    { id: 'C', text: '' },
    { id: 'D', text: '' },
  ],
  correctAnswers: [],
  explanation:    '',
  category:       '',
  source:         '',
  confidence:     'medium',
  multiSelect:    false,
  // Optional PMP-only taxonomy dimensions.  Kept on every form object for
  // simplicity; the editor only renders the dropdowns when selectedExam==='pmp'
  // so CPMAI users never see them.  '' represents "not set".
  performanceDomain: '',
  deliveryContext:   '',
  isExperimental:    false,
  // PMI-ACP ECO metadata (migrations 032 + 033). All optional.
  ecoTask:           '',
  practiceArea:      '',
  focusArea:         '',
  ecoDomain:         '',
  knowledgeArea:     '',
  artifact:          '',
  cognitiveLevel:    '',
  difficulty:        '',
  scenarioBased:     false,
  examWeight:        '',
  // Phase 3 defaults -- empty form starts as plain multiple choice with no image.
  type:              'multiple_choice',
  matching:          null,
  ordering:          null,
  image:             { src: '', alt: '' },
};

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  wrapper: {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    height: '100vh',
    overflow: 'hidden',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: '#333',
    color: '#fff',
    padding: '12px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    flex: 1,
  },
  backButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #aaa',
    borderRadius: '4px',
    color: '#eee',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  examTabBar: {
    display: 'flex',
    gap: '8px',
    padding: '14px 24px 0',
    background: '#f5f5f5',
    borderBottom: '2px solid #ddd',
    flexShrink: 0,
  },
  examTab: {
    padding: '8px 20px',
    border: '1px solid #ccc',
    borderBottom: 'none',
    borderRadius: '6px 6px 0 0',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '600',
    background: '#fff',
    color: '#444',
    position: 'relative',
    bottom: '-2px',
  },
  examTabActive: {
    background: '#333',
    color: '#fff',
    border: '1px solid #333',
    borderBottom: '2px solid #333',
  },
  body: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
  },
  // ── Sidebar (question list) ──────────────────────────────────────────────
  sidebar: {
    width: '320px',
    minWidth: '260px',
    background: '#fff',
    borderRight: '1px solid #ddd',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  searchWrapper: {
    padding: '12px',
    borderBottom: '1px solid #eee',
    flexShrink: 0,
  },
  searchInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
  },
  newQuestionButton: {
    margin: '10px 12px',
    padding: '8px 0',
    width: 'calc(100% - 24px)',
    background: '#2e7d32',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.88rem',
    fontWeight: '600',
    letterSpacing: '0.3px',
    flexShrink: 0,
  },
  questionCount: {
    padding: '6px 12px',
    fontSize: '0.78rem',
    color: '#888',
    borderBottom: '1px solid #eee',
    flexShrink: 0,
  },
  questionList: {
    flex: 1,
    overflowY: 'auto',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  questionListItem: {
    padding: '10px 16px',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '0.85rem',
    lineHeight: '1.4',
    color: '#333',
  },
  questionListItemSelected: {
    background: '#e8f0fe',
    borderLeft: '3px solid #1a73e8',
    paddingLeft: '13px',
  },
  refIdChip: {
    display: 'inline-block',
    fontFamily: "'Consolas', 'Monaco', monospace",
    fontSize: '0.7rem',
    color: '#888',
    background: '#f0f0f0',
    border: '1px solid #e0e0e0',
    borderRadius: '3px',
    padding: '1px 5px',
    marginRight: '6px',
    letterSpacing: '0.3px',
    flexShrink: 0,
    verticalAlign: 'middle',
  },
  // Small badge shown on multi-select questions in the sidebar list.
  multiSelectChip: {
    display: 'inline-block',
    fontSize: '0.65rem',
    color: '#e07b00',
    background: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: '3px',
    padding: '1px 5px',
    marginRight: '6px',
    fontWeight: '600',
    letterSpacing: '0.2px',
    flexShrink: 0,
    verticalAlign: 'middle',
  },
  // Filter row below the New Question button — hosts the multi-select toggle
  filterBar: {
    padding: '0 12px 8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  filterChip: (active) => ({
    padding: '3px 10px',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    border: `1px solid ${active ? '#e07b00' : '#ddd'}`,
    borderRadius: '12px',
    background: active ? '#fff8e1' : '#fff',
    color: active ? '#e07b00' : '#888',
    transition: 'border 0.1s, color 0.1s, background 0.1s',
  }),
  refIdInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontFamily: "'Consolas', 'Monaco', monospace",
    background: '#f5f5f5',
    color: '#888',
    boxSizing: 'border-box',
    cursor: 'default',
    letterSpacing: '0.5px',
  },
  // ── Editor pane ──────────────────────────────────────────────────────────
  editorPane: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
    background: '#fafafa',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    color: '#999',
    fontSize: '1rem',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: '#333',
    paddingBottom: '8px',
    borderBottom: '2px solid #eee',
    marginTop: 0,
  },
  savedBanner: {
    padding: '8px 16px',
    background: '#e8f5e9',
    border: '1px solid #a5d6a7',
    borderRadius: '4px',
    color: '#2e7d32',
    fontSize: '0.85rem',
    marginBottom: '16px',
  },
  errorBanner: {
    padding: '8px 16px',
    background: '#ffebee',
    border: '1px solid #ef9a9a',
    borderRadius: '4px',
    color: '#c62828',
    fontSize: '0.85rem',
    marginBottom: '16px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontWeight: '600',
    fontSize: '0.85rem',
    color: '#444',
    marginBottom: '6px',
  },
  metaLabel: {
    display: 'block',
    fontWeight: '600',
    fontSize: '0.78rem',
    color: '#999',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  metaValue: {
    fontSize: '0.82rem',
    color: '#777',
    fontFamily: "'Consolas', 'Monaco', monospace",
  },
  textarea: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  select: {
    padding: '8px 10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
    background: '#fff',
    cursor: 'pointer',
  },
  choiceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  choiceInput: {
    flex: 1,
    padding: '7px 10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
  },
  removeChoiceButton: {
    padding: '4px 8px',
    background: 'transparent',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    color: '#c62828',
    fontSize: '0.8rem',
    flexShrink: 0,
  },
  addChoiceButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #1a73e8',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    color: '#1a73e8',
    marginTop: '4px',
  },
  errorText: {
    color: '#c62828',
    fontSize: '0.8rem',
    marginTop: '4px',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem',
    color: '#444',
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
    paddingTop: '16px',
    borderTop: '1px solid #eee',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  saveButton: {
    padding: '10px 28px',
    background: '#333',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
  },
  saveButtonSaving: {
    padding: '10px 28px',
    background: '#666',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'not-allowed',
    fontSize: '1rem',
    fontWeight: '600',
    opacity: 0.7,
  },
  cancelButton: {
    padding: '10px 28px',
    background: 'transparent',
    border: '1px solid #999',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#555',
  },
  retireButton: {
    padding: '10px 20px',
    background: 'transparent',
    border: '1px solid #c62828',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#c62828',
    marginLeft: 'auto',
  },
  retireConfirmRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    background: '#fff3e0',
    border: '1px solid #ffcc80',
    borderRadius: '6px',
    fontSize: '0.85rem',
    color: '#555',
    marginLeft: 'auto',
    flexWrap: 'wrap',
  },
  retireConfirmButton: {
    padding: '6px 16px',
    background: '#c62828',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '600',
  },
  // ── Version history panel ────────────────────────────────────────────────
  historySection: {
    marginTop: '28px',
    borderTop: '1px solid #eee',
    paddingTop: '12px',
  },
  historyToggleButton: {
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    color: '#666',
    width: '100%',
    textAlign: 'left',
  },
  historyPanel: {
    marginTop: '10px',
    border: '1px solid #e8e8e8',
    borderRadius: '4px',
    overflow: 'hidden',
    background: '#fff',
  },
  historyMessage: {
    padding: '14px 16px',
    color: '#999',
    fontSize: '0.85rem',
    textAlign: 'center',
  },
  historyTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.82rem',
  },
  historyTh: {
    padding: '8px 12px',
    background: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
    textAlign: 'left',
    fontWeight: '600',
    color: '#555',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  historyTd: {
    padding: '8px 12px',
    borderBottom: '1px solid #f0f0f0',
    color: '#444',
    verticalAlign: 'middle',
  },
  historyChangeTypeBadge: (type) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '3px',
    fontSize: '0.75rem',
    fontWeight: '600',
    background: type === 'create' ? '#e8f5e9' : type === 'retire' ? '#ffebee' : type === 'restore' ? '#e3f2fd' : '#f3e5f5',
    color: type === 'create' ? '#2e7d32' : type === 'retire' ? '#c62828' : type === 'restore' ? '#1565c0' : '#6a1b9a',
  }),
  restoreButton: {
    padding: '4px 12px',
    background: '#1a73e8',
    color: '#fff',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: '600',
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Full-screen question editor backed by Supabase.
 *
 * @param {function}      onBack                  — called when the user clicks "← Back"
 * @param {string}        [initialExamId]          — pre-select this exam tab on mount
 * @param {string|null}   [initialQuestionId]      — auto-select this question after load
 * @param {boolean}       [initialFilterUnreviewed] — pre-enable the Unreviewed filter on mount
 *                                                   (used by Bulk Import so the reviewer lands
 *                                                   on a list of just-imported, unreviewed items)
 * @param {string[]}      [initialBatchIds]        — when non-empty, scopes the list to questions
 *                                                   whose `sourceBatchId` is in this set. Used
 *                                                   by Bulk Import to drop the reviewer directly
 *                                                   into just-imported content instead of the
 *                                                   whole unreviewed backlog. Dismissable via
 *                                                   a chip on the sidebar.
 * @param {object}        [questionStores]         — optional lifted stores from App.js
 *                                                   { pmp: Question[]|null, cpmai: Question[]|null }
 * @param {function}      [onQuestionStoresChange] — setter for the external stores
 */
export default function QuestionEditor({
  onBack,
  initialExamId,
  initialQuestionId,
  initialFilterUnreviewed = false,
  initialBatchIds = [],
  questionStores: propStores,
  onQuestionStoresChange,
}) {
  // Phase 4 — pull the signed-in admin's email so it can be threaded through
  // every save / lifecycle call into question_history.changed_by.  Falls
  // back to undefined when AdminGateContext isn't provided (e.g. tests that
  // mock AdminGate to render children directly without wrapping in the
  // provider).  The service layer treats undefined/null as "no caller
  // identity" and keeps the historical 'editor' default.
  const adminAuth   = useContext(AdminGateContext);
  const adminEmail  = adminAuth?.userEmail || null;
  // Internal stores — only used when external stores are NOT provided.
  const [ownStores, setOwnStores] = useState(() =>
    Object.fromEntries(getActiveCertIds().map((id) => [id, null])));

  const questionStores = propStores !== undefined ? propStores : ownStores;

  const setQuestionStores = useCallback(
    (updater) => {
      if (onQuestionStoresChange) {
        onQuestionStoresChange(updater);
      } else {
        setOwnStores(updater);
      }
    },
    [onQuestionStoresChange],
  );

  const [selectedExam, setSelectedExam] = useState(initialExamId || 'pmp');
  const pendingSelectIdRef = useRef(initialQuestionId || null);
  const sidebarListRef = useRef(null);
  // When the editor is opened via "Review imported questions →" (which sets
  // initialFilterUnreviewed=true without a specific initialQuestionId), we
  // auto-select the first unreviewed question so the reviewer lands directly
  // on something to review instead of the empty-selection placeholder. This
  // ref ensures we only auto-select once per mount so the user can freely
  // deselect afterwards.
  const didAutoSelectUnreviewedRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState('');
  // PR 2B: lazy fallback load status when the lifted store hasn't populated yet.
  const [bankLoadError, setBankLoadError] = useState(null);
  const [filterMultiSelect, setFilterMultiSelect] = useState(false);
  const [filterUncategorized, setFilterUncategorized] = useState(false);
  const [filterUnreviewed, setFilterUnreviewed] = useState(Boolean(initialFilterUnreviewed));
  // When non-empty, filter the sidebar to questions whose sourceBatchId is in
  // this set. Initialized from the initialBatchIds prop (Bulk Import hand-off)
  // and cleared by the "Clear batch filter" chip. Stored as an array for
  // stable useMemo deps.
  const [filterBatchIds, setFilterBatchIds] = useState(
    Array.isArray(initialBatchIds) ? initialBatchIds : []
  );
  const [selectedId, setSelectedId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [savedId, setSavedId] = useState(null);

  // ── Phase 2 state ─────────────────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle'|'saving'|'saved'|'error'
  const [saveError, setSaveError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [questionHistory, setQuestionHistory] = useState(null); // null = not fetched
  const [confirmRetire, setConfirmRetire] = useState(false);

  // ── Lifecycle state ───────────────────────────────────────────────────────
  // Tracks in-flight lifecycle RPC (approve / reviewStatus change)
  const [lifecycleStatus, setLifecycleStatus]   = useState('idle'); // 'idle'|'saving'|'saved'|'error'
  const [lifecycleError, setLifecycleError]     = useState(null);
  // Draft reviewStatus while the dropdown is open (only committed on "Save")
  const [reviewStatusDraft, setReviewStatusDraft] = useState(null); // null = use question value

  // Phase 3.5 — Supabase Storage image upload state.
  // imageUploadStatus: 'idle' | 'uploading' | 'success' | 'error'
  const imageFileInputRef = useRef(null);
  const [imageUploadStatus, setImageUploadStatus] = useState('idle');
  const [imageUploadError,  setImageUploadError]  = useState(null);

  // Phase 3.6 — preview overlay state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copyLinkStatus, setCopyLinkStatus] = useState('idle'); // idle|copied|error

  // Builds a question-shaped object from the current form state so preview
  // reflects in-progress edits (not just the canonical stored version).
  function buildPreviewQuestion() {
    // Resolve referenceId from the in-memory store (the Editor doesn't keep
    // an `existing` reference at component scope — it derives it inside each
    // handler).  Falls back through formData.referenceId then 'PREVIEW'.
    const storeMatch = (questionStores[selectedExam] ?? []).find((q) => q.id === selectedId);
    // Pass through any image with a non-empty src so the preview matches the
    // learner experience: QuestionCard renders the exhibit whenever
    // image.src is set, regardless of the question's `type`.  Earlier this
    // gated on type === 'visual_interpretation', which broke previews for
    // questions that have an image but a different stored type (the most
    // common cause is the RPC's COALESCE preserving a previously-uploaded
    // image after the type was changed back to multiple_choice).
    const hasImageSrc = !!(formData.image?.src && String(formData.image.src).trim());
    return {
      id: selectedId || 'preview',
      referenceId: storeMatch?.referenceId || formData.referenceId || 'PREVIEW',
      type: formData.type ?? (formData.multiSelect ? 'multiple_select' : 'multiple_choice'),
      question: formData.question || '',
      choices: interactivePayload(formData).choices || [],
      correctAnswers: interactivePayload(formData).correctAnswers || [],
      explanation: formData.explanation || '',
      category: formData.category || '',
      multiSelect: formData.multiSelect === true,
      image: hasImageSrc
        ? { src: formData.image.src, alt: formData.image.alt || '' }
        : null,
    };
  }

  async function handleCopyPreviewLink() {
    if (!selectedId) {
      setCopyLinkStatus('error');
      return;
    }
    const url = buildPreviewUrl(selectedId, selectedExam);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for environments without the Clipboard API (older browsers,
        // some corp restrictions).  Uses a temporary textarea + execCommand.
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopyLinkStatus('copied');
      setTimeout(() => setCopyLinkStatus('idle'), 1800);
    } catch (err) {
      setCopyLinkStatus('error');
      setTimeout(() => setCopyLinkStatus('idle'), 2000);
    }
  }

  async function handleImageUpload(file) {
    setImageUploadStatus('uploading');
    setImageUploadError(null);
    try {
      const { publicUrl } = await uploadQuestionImage(file, {
        examId: selectedExam,
        questionId: selectedId || 'new',
      });
      // Auto-fill the URL field; preserve any alt text already entered.
      handleFieldChange('image', {
        ...(formData.image ?? {}),
        src: publicUrl,
      });
      // Phase 3.7 — when the user uploads an image, switch the question
      // type to 'visual_interpretation' so the exhibit is the canonical
      // shape and the data model stays honest (image only on visual rows).
      // We don't override 'multiple_select' because multi-select is a
      // deliberate setting — the user can manually flip the dropdown if
      // they actually want a multi-select with an exhibit.
      if (!formData.type || formData.type === 'multiple_choice') {
        handleFieldChange('type', 'visual_interpretation');
        handleFieldChange('multiSelect', false);
      }
      setImageUploadStatus('success');
    } catch (err) {
      const msg = err instanceof UploadValidationError
        ? err.message
        : (err?.message || 'Upload failed for an unknown reason.');
      setImageUploadError(msg);
      setImageUploadStatus('error');
    }
  }

  // ── Load questions + deep-link ─────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;

    function applyDeepLink(resolvedQuestions) {
      if (cancelled || !pendingSelectIdRef.current) return;
      const match = (resolvedQuestions || []).find((q) => q.id === pendingSelectIdRef.current);
      if (match) {
        setSelectedId(match.id);
        // Single source of truth for question → form mapping (also handles
        // the interactive types' object-shaped choices).
        setFormData(formDataFromQuestion(match));
      }
      pendingSelectIdRef.current = null;
    }

    if (questionStores[selectedExam] === null) {
      // Lazily load JUST this cert's bank (PR 2B — no static loadQuestions).
      // The load result seeds whichever store is authoritative (see below).
      setBankLoadError(null);
      (async () => {
        let resolvedQuestions;
        try {
          resolvedQuestions = (await ensureCertLoaded(selectedExam)).questions;
        } catch (err) {
          if (!cancelled) {
            console.warn('[QuestionEditor] bank load failed:', err);
            setBankLoadError(`Could not load the ${selectedExam.toUpperCase()} question bank: ${err?.message || 'unknown error'}.`);
          }
          return;
        }
        if (cancelled) return;
        // Seed the authoritative store once so the list actually renders —
        // non-clobber guarded (same rule as App.applyLoadedBank), so it never
        // loops or overwrites a populated slot.
        //
        // The LIFTED store must be seeded too: since the metadata-pool
        // rollout (stage 3, PR 2), learner session starts on pool-enabled
        // certs no longer populate questionStores, so the editor is often the
        // FIRST full-bank consumer — staying render-only left the list empty.
        // (The historical "don't seed the shared store" rule predates
        // ensureCertLoaded: back then the editor's own load was the bundled
        // JSON and seeding would have pinned the store to the bundle. Today
        // ensureCertLoaded IS the authoritative Supabase bank, with the same
        // bundled fallback App itself uses.)
        if (propStores === undefined) {
          setOwnStores((prev) =>
            (prev[selectedExam] == null ? { ...prev, [selectedExam]: resolvedQuestions } : prev));
        } else {
          setQuestionStores((prev) =>
            (prev[selectedExam] == null ? { ...prev, [selectedExam]: resolvedQuestions } : prev));
        }
        applyDeepLink(resolvedQuestions);
      })();
    } else {
      applyDeepLink(questionStores[selectedExam]);
    }

    return () => { cancelled = true; };
  }, [selectedExam, questionStores, setQuestionStores, propStores]);

  const questions = useMemo(
    () => questionStores[selectedExam] ?? [],
    [questionStores, selectedExam]
  );

  const filteredQuestions = useMemo(() => {
    // Always hide quarantined (removed) questions from the editor list
    let result = questions.filter((q) => q.reviewStatus !== 'removed');
    if (filterMultiSelect) {
      result = result.filter((q) => q.multiSelect === true);
    }
    if (filterUncategorized) {
      result = result.filter((q) => !q.category || !q.category.trim());
    }
    if (filterUnreviewed) {
      // An "unreviewed" question is one whose reviewStatus is explicitly
      // 'unreviewed' — or missing/unknown (defensive fallback for older rows
      // that predate the reviewStatus column).
      result = result.filter(
        (q) => !q.reviewStatus || q.reviewStatus === 'unreviewed'
      );
    }
    if (filterBatchIds.length > 0) {
      const batchSet = new Set(filterBatchIds);
      result = result.filter((q) => batchSet.has(q.sourceBatchId));
    }
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (q) =>
          q.question.toLowerCase().includes(lower) ||
          (q.referenceId && q.referenceId.toLowerCase().includes(lower))
      );
    }
    return result;
  }, [questions, searchTerm, filterMultiSelect, filterUncategorized, filterUnreviewed, filterBatchIds]);

  const uncategorizedCount = useMemo(
    () => questions.filter((q) => !q.category || !q.category.trim()).length,
    [questions]
  );

  const unreviewedCount = useMemo(
    () =>
      questions.filter(
        (q) =>
          q.reviewStatus !== 'removed' &&
          (!q.reviewStatus || q.reviewStatus === 'unreviewed')
      ).length,
    [questions]
  );

  const selectedReferenceId = useMemo(
    () => questions.find((q) => q.id === selectedId)?.referenceId ?? '',
    [questions, selectedId]
  );

  // Auto-select the first unreviewed question when the editor is opened via
  // "Review imported questions →". We wait until filteredQuestions populates
  // (questionStores can load asynchronously from Supabase) before selecting.
  useEffect(() => {
    if (didAutoSelectUnreviewedRef.current) return;
    if (!initialFilterUnreviewed) return;
    if (initialQuestionId) return;             // explicit target wins
    if (selectedId) return;                    // user already picked something
    if (filteredQuestions.length === 0) return; // nothing yet — wait
    handleSelectQuestion(filteredQuestions[0]);
    didAutoSelectUnreviewedRef.current = true;
    // handleSelectQuestion is intentionally omitted from deps — we only want
    // to auto-select once per mount, not re-fire when the function identity
    // changes on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredQuestions, initialFilterUnreviewed, initialQuestionId, selectedId]);

  // ── Sidebar navigation helpers ─────────────────────────────────────────────
  const selectedIndexInFiltered = useMemo(
    () => filteredQuestions.findIndex((q) => q.id === selectedId),
    [filteredQuestions, selectedId]
  );
  // Enable Prev/Next whenever there are filtered questions. When no question
  // is currently selected (selectedIndexInFiltered === -1), Prev jumps to the
  // last question and Next jumps to the first — this makes the nav strip
  // useful on any editor entry path, not just the Bulk Import auto-select path.
  // When a question IS selected, we still grey out at the boundaries so the
  // user gets the usual end-of-list signal.
  const hasPrev =
    filteredQuestions.length > 0 && selectedIndexInFiltered !== 0;
  const hasNext =
    filteredQuestions.length > 0 &&
    selectedIndexInFiltered !== filteredQuestions.length - 1;

  function handlePrev() {
    if (!hasPrev) return;
    if (selectedIndexInFiltered === -1) {
      handleSelectQuestion(filteredQuestions[filteredQuestions.length - 1]);
    } else {
      handleSelectQuestion(filteredQuestions[selectedIndexInFiltered - 1]);
    }
  }
  function handleNext() {
    if (!hasNext) return;
    if (selectedIndexInFiltered === -1) {
      handleSelectQuestion(filteredQuestions[0]);
    } else {
      handleSelectQuestion(filteredQuestions[selectedIndexInFiltered + 1]);
    }
  }

  // Auto-scroll the sidebar so the selected item stays visible
  useEffect(() => {
    if (!sidebarListRef.current) return;
    const el = sidebarListRef.current.querySelector('[data-selected="true"]');
    if (el) el.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Resets all Phase 2 transient state. Call when changing selection or exam. */
  function resetPhase2State() {
    setSaveStatus('idle');
    setSaveError(null);
    setShowHistory(false);
    setQuestionHistory(null);
    setConfirmRetire(false);
    setLifecycleStatus('idle');
    setLifecycleError(null);
    setReviewStatusDraft(null);
  }

  /**
   * Calls the update_question_lifecycle RPC to change status and/or reviewStatus.
   * Updates the local store from the DB response.
   *
   * ── Approve-with-unsaved-edits ──────────────────────
   * When the target is `status='active'` (i.e. the user clicked Approve)
   * and the editor form has unsaved edits, we save the content via
   * `updateQuestionWithHistory` FIRST, then flip the lifecycle.  This keeps
   * Approve from promoting a question while the DB still holds the pre-edit
   * content.  If validation fails or the save RPC errors, the lifecycle
   * flip is skipped and the error is surfaced so the user can fix it.
   *
   * @param {{ status?: string, reviewStatus?: string, changeNote?: string }} params
   */
  async function handleLifecycleUpdate({ status, reviewStatus, changeNote = '' }) {
    setLifecycleStatus('saving');
    setLifecycleError(null);

    try {
      const existing = (questionStores[selectedExam] ?? []).find((q) => q.id === selectedId) ?? {};

      // ── Save content first if approving a dirty form ─────────────
      const needsContentSave =
        status === 'active' &&
        selectedId &&
        formData &&
        isFormDirty(formData, existing);

      // `working` is the canonical question used as the base for the lifecycle
      // response merge below.  Starts as whatever is currently in the store;
      // if we save content first, it's replaced with the saved canonical.
      let working = existing;

      if (needsContentSave) {
        const errs = validateForm(formData, selectedExam);
        if (Object.keys(errs).length > 0) {
          setErrors(errs);
          setLifecycleStatus('error');
          setLifecycleError('Fix validation errors before approving.');
          return;
        }

        const savedRaw = await updateQuestionWithHistory({
          id:                selectedId,
          examId:            selectedExam,
          question:          formData.question.trim(),
          choices:           formData.choices,
          correctAnswers:    formData.correctAnswers,
          explanation:       formData.explanation,
          category:          formData.category,
          confidence:        formData.confidence,
          multiSelect:       formData.multiSelect,
          type:              formData.type ?? (formData.multiSelect ? 'multiple_select' : 'multiple_choice'),
          image:             formData.type === 'visual_interpretation'
            ? { src: formData.image?.src || '', alt: formData.image?.alt || '' }
            : null,
          changeNote:        formData.changeNote || 'Saved with Approve',
          performanceDomain: selectedExam === 'pmp' ? (formData.performanceDomain || null) : null,
          deliveryContext:   selectedExam === 'pmp' ? (formData.deliveryContext   || null) : null,
          isExperimental:    formData.isExperimental === true,
          // PMI-ACP ECO metadata (migrations 032 + 033). ecoDomain syncs with category.
          ecoDomain:         selectedExam === 'pmi-acp' ? (formData.category       || null)
                             : isEcoDomainRequired(selectedExam) ? (formData.ecoDomain || null) : null,
          ecoTask:           selectedExam === 'pmi-acp' ? (formData.ecoTask        || null) : null,
          practiceArea:      selectedExam === 'pmi-acp' ? (formData.practiceArea   || null) : null,
          focusArea:         SECONDARY_DIMENSION_VALUES[selectedExam] ? (formData.focusArea || null) : null,
          knowledgeArea:     selectedExam === 'pmi-acp' ? (formData.knowledgeArea  || null) : null,
          artifact:          selectedExam === 'pmi-acp' ? (formData.artifact       || null) : null,
          cognitiveLevel:    selectedExam === 'pmi-acp' ? (formData.cognitiveLevel || null) : null,
          difficulty:        selectedExam === 'pmi-acp' ? (formData.difficulty     || null) : null,
          scenarioBased:     selectedExam === 'pmi-acp' ? (formData.scenarioBased === true) : false,
          examWeight:        selectedExam === 'pmi-acp' ? (formData.examWeight     || null) : null,
          source:            formData.source || null,
          changedBy:         adminEmail,
        });

        const savedCanonical = savedRaw
          ? { ...existing, ...normalizeSupabaseQuestion(savedRaw, selectedExam) }
          : {
              ...existing,
              question:          formData.question.trim(),
              choices:           formData.choices,
              correctAnswers:    formData.correctAnswers,
              explanation:       formData.explanation,
              category:          formData.category,
              confidence:        formData.confidence,
              multiSelect:       formData.multiSelect,
          type:              formData.type ?? (formData.multiSelect ? 'multiple_select' : 'multiple_choice'),
          image:             formData.type === 'visual_interpretation'
            ? { src: formData.image?.src || '', alt: formData.image?.alt || '' }
            : null,
              performanceDomain: selectedExam === 'pmp' ? (formData.performanceDomain || null) : null,
              deliveryContext:   selectedExam === 'pmp' ? (formData.deliveryContext   || null) : null,
              source:            formData.source || null,
              id:                selectedId,
            };

        // Push the saved content into the store so the UI (and `existing`
        // lookups elsewhere) stay consistent between the two RPCs.
        setQuestionStores((prev) => {
          const list = prev[selectedExam] ?? [];
          const updated = list.map((q) => q.id === selectedId ? savedCanonical : q);
          return { ...prev, [selectedExam]: updated };
        });

        // Refresh formData from the saved canonical so trimming, server
        // timestamps, version bumps, and the cleared changeNote are reflected.
        setFormData(formDataFromQuestion(savedCanonical));
        setErrors({});
        working = savedCanonical;
      }

      // ── Lifecycle flip (the existing behaviour) ──────────────────
      const persisted = await updateQuestionLifecycle({
        id: selectedId,
        status: status || null,
        reviewStatus: reviewStatus || null,
        changeNote,
        changedBy: adminEmail,
      });

      const canonical = persisted
        ? { ...working, ...normalizeSupabaseQuestion(persisted, selectedExam) }
        : { ...working, ...(status ? { status } : {}), ...(reviewStatus ? { reviewStatus } : {}) };

      const isQuarantined = reviewStatus === 'removed';
      setQuestionStores((prev) => {
        const list = prev[selectedExam] ?? [];
        // Remove from the in-memory list when retired or quarantined (removed)
        const updated = (status === 'retired' || isQuarantined)
          ? list.filter((q) => q.id !== selectedId)
          : list.map((q) => q.id === selectedId ? canonical : q);
        return { ...prev, [selectedExam]: updated };
      });

      if (status !== 'retired' && !isQuarantined) {
        // For plain lifecycle-only changes (review-status updates, Approve on
        // a clean form), intentionally NOT rebuilding formData from canonical
        // so any unsaved edits the user has in the dropdowns / text fields
        // survive.  When we reached here via the save-first branch, formData
        // was already refreshed above from the saved canonical.
      } else {
        setSelectedId(null);
        setFormData(null);
      }

      setReviewStatusDraft(null);
      setLifecycleStatus('saved');
      setQuestionHistory(null); // invalidate history panel

    } catch (err) {
      setLifecycleStatus('error');
      setLifecycleError(err.message || 'Lifecycle update failed. Please try again.');
    }
  }

  /** Initializes formData from a question object. */
  function formDataFromQuestion(q) {
    // Interactive types carry object-shaped choices — those live in their own
    // form slots (below); the classic fields stay safely defaulted.
    const interactive = interactiveFormState(q);
    return {
      ...interactive,
      question:       q.question,
      choices:        (Array.isArray(q.choices) ? q.choices : []).map((c) => ({ ...c })),
      correctAnswers: INTERACTIVE_TYPES.has(q.type) ? [] : [...(q.correctAnswers ?? [])],
      explanation:    q.explanation ?? '',
      category:       q.category ?? '',
      source:         q.source ?? '',
      confidence:     q.confidence ?? 'medium',
      multiSelect:    q.multiSelect ?? false,
      // '' represents "not set".  These stay '' on CPMAI rows (where the
      // dropdowns aren't rendered) so they survive a round-trip without
      // being overwritten to null.
      performanceDomain: q.performanceDomain ?? '',
      deliveryContext:   q.deliveryContext   ?? '',
      isExperimental:    q.isExperimental === true,
      // PMI-ACP ECO metadata (migrations 032 + 033).
      ecoTask:           q.ecoTask        ?? '',
      practiceArea:      q.practiceArea   ?? '',
    focusArea:         q.focusArea      ?? '',
    ecoDomain:         q.ecoDomain      ?? '',
      knowledgeArea:     q.knowledgeArea  ?? '',
      artifact:          q.artifact       ?? '',
      cognitiveLevel:    q.cognitiveLevel ?? '',
      difficulty:        q.difficulty     ?? '',
      scenarioBased:     q.scenarioBased === true,
      examWeight:        q.examWeight     ?? '',
      type:              q.type ?? (q.multiSelect ? 'multiple_select' : 'multiple_choice'),
      image:             {
        src: q.image?.src ?? '',
        alt: q.image?.alt ?? '',
      },
      changeNote:        '',
    };
  }

  // ── Handlers ───────────────────────────────────────────────────────────────

  function handleSelectQuestion(q) {
    setIsCreating(false);
    setSelectedId(q.id);
    setFormData(formDataFromQuestion(q));
    setErrors({});
    setSavedId(null);
    resetPhase2State();
  }

  function handleExamChange(examId) {
    setSelectedExam(examId);
    setSelectedId(null);
    setIsCreating(false);
    setFormData(null);
    setErrors({});
    setSearchTerm('');
    setFilterMultiSelect(false);
    setFilterUncategorized(false);
    setFilterUnreviewed(false);
    setSavedId(null);
    resetPhase2State();
  }

  function handleNewQuestion() {
    setSelectedId(null);
    setIsCreating(true);
    setFormData({ ...BLANK_FORM, choices: BLANK_FORM.choices.map((c) => ({ ...c })) });
    setErrors({});
    setSavedId(null);
    resetPhase2State();
  }

  function handleFieldChange(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  function handleChoiceText(index, text) {
    setFormData((prev) => {
      const choices = prev.choices.map((c, i) => (i === index ? { ...c, text } : c));
      return { ...prev, choices };
    });
    if (errors.choices) {
      setErrors((prev) => { const e = { ...prev }; delete e.choices; return e; });
    }
  }

  function handleCorrectToggle(choiceId) {
    setFormData((prev) => {
      const already = prev.correctAnswers.includes(choiceId);
      const newCorrect = already
        ? prev.correctAnswers.filter((id) => id !== choiceId)
        : [...prev.correctAnswers, choiceId];
      return { ...prev, correctAnswers: newCorrect };
    });
    if (errors.correctAnswers) {
      setErrors((prev) => { const e = { ...prev }; delete e.correctAnswers; return e; });
    }
  }

  function handleAddChoice() {
    setFormData((prev) => ({ ...prev, choices: addChoice(prev.choices) }));
  }

  function handleRemoveChoice(index) {
    setFormData((prev) => {
      const { choices, correctAnswers } = removeChoice(prev.choices, index, prev.correctAnswers);
      return { ...prev, choices, correctAnswers };
    });
  }

  /**
   * Await Supabase persistence.  Updates the local store from the DB response so
   * the UI reflects the actual persisted state (version number, timestamp, etc.).
   * Shows an error banner if the write fails instead of silently continuing.
   */
  async function handleSave() {
    if (isCreating) { await handleSaveNew(); return; }

    const original = (questionStores[selectedExam] ?? []).find((q) => q.id === selectedId) ?? null;
    const errs = validateForm(formData, selectedExam, original);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaveStatus('saving');
    setSaveError(null);

    const existing = (questionStores[selectedExam] ?? []).find((q) => q.id === selectedId) ?? {};

    try {
      // Await Supabase RPC — throws on network or DB error.
      // PMP-only taxonomy fields are forwarded only for the PMP exam; for
      // CPMAI they stay null so existing CPMAI behaviour is unchanged.
      const persisted = await updateQuestionWithHistory({
        id:             selectedId,
        examId:         selectedExam,
        question:       formData.question.trim(),
        // interactivePayload passes classic fields through untouched and
        // builds the object choices + key for the interactive types.
        choices:        interactivePayload(formData).choices,
        correctAnswers: interactivePayload(formData).correctAnswers,
        explanation:    formData.explanation,
        category:       formData.category,
        confidence:     formData.confidence,
        multiSelect:    INTERACTIVE_TYPES.has(formData.type) ? false : formData.multiSelect,
        type:           formData.type ?? (formData.multiSelect ? 'multiple_select' : 'multiple_choice'),
        image:          formData.type === 'visual_interpretation'
          ? { src: formData.image?.src || '', alt: formData.image?.alt || '' }
          : null,
        changeNote:     formData.changeNote || '',
        performanceDomain: selectedExam === 'pmp' ? (formData.performanceDomain || null) : null,
        deliveryContext:   selectedExam === 'pmp' ? (formData.deliveryContext   || null) : null,
        isExperimental:    formData.isExperimental === true,
        // PMI-ACP ECO metadata (migrations 032 + 033). ecoDomain syncs with category.
        ecoDomain:         selectedExam === 'pmi-acp' ? (formData.category       || null)
                             : isEcoDomainRequired(selectedExam) ? (formData.ecoDomain || null) : null,
        ecoTask:           selectedExam === 'pmi-acp' ? (formData.ecoTask        || null) : null,
        practiceArea:      selectedExam === 'pmi-acp' ? (formData.practiceArea   || null) : null,
        focusArea:         SECONDARY_DIMENSION_VALUES[selectedExam] ? (formData.focusArea || null) : null,
        knowledgeArea:     selectedExam === 'pmi-acp' ? (formData.knowledgeArea  || null) : null,
        artifact:          selectedExam === 'pmi-acp' ? (formData.artifact       || null) : null,
        cognitiveLevel:    selectedExam === 'pmi-acp' ? (formData.cognitiveLevel || null) : null,
        difficulty:        selectedExam === 'pmi-acp' ? (formData.difficulty     || null) : null,
        scenarioBased:     selectedExam === 'pmi-acp' ? (formData.scenarioBased === true) : false,
        examWeight:        selectedExam === 'pmi-acp' ? (formData.examWeight     || null) : null,
        source:            formData.source || null,
        changedBy:         adminEmail,
      });

      // Build the canonical store entry from DB response (or local merge as fallback
      // for environments where the RPC is stubbed out, e.g. unit tests)
      const canonical = persisted
        ? { ...existing, ...normalizeSupabaseQuestion(persisted, selectedExam) }
        : {
            ...existing,
            question:       formData.question.trim(),
            choices:        interactivePayload(formData).choices,
            correctAnswers: interactivePayload(formData).correctAnswers,
            explanation:    formData.explanation,
            category:       formData.category,
            confidence:     formData.confidence,
            multiSelect:    INTERACTIVE_TYPES.has(formData.type) ? false : formData.multiSelect,
            performanceDomain: selectedExam === 'pmp' ? (formData.performanceDomain || null) : null,
            deliveryContext:   selectedExam === 'pmp' ? (formData.deliveryContext   || null) : null,
            source:            formData.source || null,
            id:             selectedId,
          };

      setQuestionStores((prev) => {
        const list = prev[selectedExam] ?? [];
        const updated = list.map((q) => q.id === selectedId ? canonical : q);
        return { ...prev, [selectedExam]: updated };
      });

      // Refresh formData from persisted values
      setFormData(formDataFromQuestion(canonical));

      setSaveStatus('saved');
      setSavedId(selectedId);
      setErrors({});
      setQuestionHistory(null); // invalidate — will reload on next history open
      setShowHistory(false);

    } catch (err) {
      setSaveStatus('error');
      setSaveError(err.message || 'Failed to save. Please try again.');
    }
  }

  /**
   * Validate formData, generate a new ID, persist to Supabase, then transition
   * to edit mode with the new question selected.
   * Only updates the local store after the DB write succeeds.
   */
  async function handleSaveNew() {
    const errs = validateForm(formData, selectedExam);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaveStatus('saving');
    setSaveError(null);

    let newId = generateNewQuestionId(selectedExam, questions);
    let refId = generateReferenceId(selectedExam, newId);

    // Defensive retry: the in-memory store can be stale (e.g. an earlier
    // loader filter excluded visual_interpretation rows from the cache),
    // so the optimistic newId can collide with an existing row.  On the
    // duplicate-PK error we re-derive the next id from Supabase directly
    // and retry once, transparent to the user.
    function isDuplicatePkError(err) {
      const msg = String(err?.message || '');
      return msg.includes('duplicate key') || msg.includes('questions_pkey') || msg.includes('23505');
    }
    async function recoverAndRetryWithFreshId() {
      const maxNum = await fetchMaxQuestionIdNumber(selectedExam);
      newId = `${selectedExam}-${String(maxNum + 1).padStart(4, '0')}`;
      refId = generateReferenceId(selectedExam, newId);
      console.warn(`[QuestionEditor] id collision on create — retrying with ${newId}`);
    }

    try {
      let persisted;
      let attempts = 0;
      while (true) {
        attempts++;
        try {
          persisted = await createQuestionWithHistory({
        id:             newId,
        examId:         selectedExam,
        referenceId:    refId,
        question:       formData.question.trim(),
        // interactivePayload passes classic fields through untouched and
        // builds the object choices + key for the interactive types.
        choices:        interactivePayload(formData).choices,
        correctAnswers: interactivePayload(formData).correctAnswers,
        explanation:    formData.explanation,
        category:       formData.category,
        confidence:     formData.confidence,
        multiSelect:    INTERACTIVE_TYPES.has(formData.type) ? false : formData.multiSelect,
        type:           formData.type ?? (formData.multiSelect ? 'multiple_select' : 'multiple_choice'),
        image:          formData.type === 'visual_interpretation'
          ? { src: formData.image?.src || '', alt: formData.image?.alt || '' }
          : null,
        changeNote:     'Initial creation',
        performanceDomain: selectedExam === 'pmp' ? (formData.performanceDomain || null) : null,
        deliveryContext:   selectedExam === 'pmp' ? (formData.deliveryContext   || null) : null,
        isExperimental:    formData.isExperimental === true,
        // PMI-ACP ECO metadata (migrations 032 + 033). ecoDomain syncs with category.
        ecoDomain:         selectedExam === 'pmi-acp' ? (formData.category       || null)
                             : isEcoDomainRequired(selectedExam) ? (formData.ecoDomain || null) : null,
        ecoTask:           selectedExam === 'pmi-acp' ? (formData.ecoTask        || null) : null,
        practiceArea:      selectedExam === 'pmi-acp' ? (formData.practiceArea   || null) : null,
        focusArea:         SECONDARY_DIMENSION_VALUES[selectedExam] ? (formData.focusArea || null) : null,
        knowledgeArea:     selectedExam === 'pmi-acp' ? (formData.knowledgeArea  || null) : null,
        artifact:          selectedExam === 'pmi-acp' ? (formData.artifact       || null) : null,
        cognitiveLevel:    selectedExam === 'pmi-acp' ? (formData.cognitiveLevel || null) : null,
        difficulty:        selectedExam === 'pmi-acp' ? (formData.difficulty     || null) : null,
        scenarioBased:     selectedExam === 'pmi-acp' ? (formData.scenarioBased === true) : false,
        examWeight:        selectedExam === 'pmi-acp' ? (formData.examWeight     || null) : null,
        source:            formData.source || null,
        changedBy:         adminEmail,
        });
          break; // success — exit retry loop
        } catch (err) {
          if (attempts < 2 && isDuplicatePkError(err)) {
            await recoverAndRetryWithFreshId();
            continue;
          }
          throw err;
        }
      }

      const canonical = persisted
        ? normalizeSupabaseQuestion(persisted, selectedExam)
        : {
            id:             newId,
            referenceId:    refId,
            type:           formData.type ?? (formData.multiSelect ? 'multiple_select' : 'multiple_choice'),
            image:          formData.type === 'visual_interpretation'
              ? { src: formData.image?.src || '', alt: formData.image?.alt || '' }
              : null,
            question:       formData.question.trim(),
            choices:        interactivePayload(formData).choices,
            correctAnswers: interactivePayload(formData).correctAnswers,
            explanation:    formData.explanation,
            category:       formData.category,
            source:         formData.source,
            confidence:     formData.confidence,
            multiSelect:    INTERACTIVE_TYPES.has(formData.type) ? false : formData.multiSelect,
            performanceDomain: selectedExam === 'pmp' ? (formData.performanceDomain || null) : null,
            deliveryContext:   selectedExam === 'pmp' ? (formData.deliveryContext   || null) : null,
            status:         'active',
          };

      setQuestionStores((prev) => {
        const list = prev[selectedExam] ?? [];
        return { ...prev, [selectedExam]: [...list, canonical] };
      });

      setIsCreating(false);
      setSelectedId(newId);
      setFormData(formDataFromQuestion(canonical));
      setSaveStatus('saved');
      setSavedId(newId);
      setErrors({});

    } catch (err) {
      setSaveStatus('error');
      setSaveError(err.message || 'Failed to create question. Please try again.');
    }
  }

  /**
   * Retire the currently selected question.
   * Sets status = 'retired' in Supabase via the retire_question RPC,
   * then removes the question from the local store (retired questions are
   * excluded from live exams and no longer shown in the editor list).
   */
  async function handleRetire() {
    setConfirmRetire(false);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      await retireQuestion(selectedId, 'Retired via Question Editor', adminEmail);

      // Remove from local store so it disappears from the sidebar immediately
      setQuestionStores((prev) => {
        const list = prev[selectedExam] ?? [];
        return { ...prev, [selectedExam]: list.filter((q) => q.id !== selectedId) };
      });

      setSelectedId(null);
      setFormData(null);
      setSaveStatus('idle');
      setSavedId(null);

    } catch (err) {
      setSaveStatus('error');
      setSaveError(err.message || 'Failed to retire question. Please try again.');
    }
  }

  /**
   * Fetches the version history for a question from Supabase and populates
   * the history panel.  Sets questionHistory to [] if none found or on error.
   */
  async function loadHistoryForQuestion(questionId) {
    setQuestionHistory(null); // null = loading indicator
    try {
      const history = await fetchQuestionHistory(questionId);
      setQuestionHistory(Array.isArray(history) ? history : []);
    } catch (err) {
      console.warn('[QuestionEditor] Failed to load history:', err);
      setQuestionHistory([]);
    }
  }

  /**
   * Restore the question to the state captured in a given history entry.
   * Calls restoreQuestionVersion RPC then refreshes local state from the
   * returned DB row.
   *
   * @param {{ id: string, versionNo: number }} historyEntry
   */
  async function handleRestore(historyEntry) {
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const persisted = await restoreQuestionVersion(
        selectedId,
        historyEntry.id,
        `Restored to version ${historyEntry.versionNo}`,
        adminEmail,
      );

      if (persisted) {
        const existing = (questionStores[selectedExam] ?? []).find((q) => q.id === selectedId) ?? {};
        const canonical = { ...existing, ...normalizeSupabaseQuestion(persisted, selectedExam) };

        setQuestionStores((prev) => {
          const list = prev[selectedExam] ?? [];
          const updated = list.map((q) => q.id === selectedId ? canonical : q);
          return { ...prev, [selectedExam]: updated };
        });

        setFormData(formDataFromQuestion(canonical));
      }

      setSaveStatus('saved');
      setSavedId(selectedId);
      setShowHistory(false);
      setQuestionHistory(null); // invalidate — will reload on next history open

    } catch (err) {
      setSaveStatus('error');
      setSaveError(err.message || 'Failed to restore version. Please try again.');
    }
  }

  /**
   * In edit mode: reset to the last saved state of the selected question.
   * In create mode: discard the blank form and return to the empty state.
   */
  function handleCancel() {
    if (isCreating) {
      setIsCreating(false);
      setFormData(null);
      setErrors({});
      resetPhase2State();
      return;
    }
    const list = questionStores[selectedExam] ?? [];
    const q = list.find((item) => item.id === selectedId);
    if (q) handleSelectQuestion(q);
  }

  // ── Toggle history panel ───────────────────────────────────────────────────

  function handleHistoryToggle() {
    if (showHistory) {
      setShowHistory(false);
    } else {
      setShowHistory(true);
      loadHistoryForQuestion(selectedId);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const isSaving = saveStatus === 'saving';

  return (
    <div data-testid="question-editor" style={styles.wrapper}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={styles.header}>
        <button
          data-testid="editor-back-button"
          style={styles.backButton}
          onClick={onBack}
        >
          ← Back
        </button>
        <span style={styles.headerTitle}>✏️ Question Editor</span>
      </div>

      {/* ── Exam selector tabs ───────────────────────────────────────────── */}
      {/* Driven from getAllExams() so new certifications added in
          src/config/exams.js show up automatically. */}
      <div style={styles.examTabBar}>
        {getAllExams().map((exam) => (
          <button
            key={exam.examId}
            data-testid={`editor-exam-${exam.examId}`}
            style={{
              ...styles.examTab,
              ...(selectedExam === exam.examId ? styles.examTabActive : {}),
            }}
            onClick={() => handleExamChange(exam.examId)}
          >
            {exam.displayName}
          </button>
        ))}
      </div>

      {/* ── Two-panel body ───────────────────────────────────────────────── */}
      <div style={styles.body}>
        {/* Left — question list */}
        <div style={styles.sidebar}>
          <div style={styles.searchWrapper}>
            <input
              data-testid="editor-search"
              style={styles.searchInput}
              type="text"
              placeholder="Search questions…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {bankLoadError && (
            <div data-testid="editor-bank-load-error" role="alert" style={{
              margin: '0 0 8px', padding: '8px 10px', fontSize: '0.85rem',
              color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
            }}>
              {bankLoadError}
            </div>
          )}
          <button
            data-testid="editor-new-question-button"
            style={styles.newQuestionButton}
            onClick={handleNewQuestion}
          >
            + New Question
          </button>
          {/* Filter chips */}
          <div style={styles.filterBar}>
            <button
              data-testid="editor-filter-multiselect"
              style={styles.filterChip(filterMultiSelect)}
              onClick={() => setFilterMultiSelect((v) => !v)}
              aria-pressed={filterMultiSelect}
              title="Show only multi-select questions"
            >
              {filterMultiSelect ? '✕ ' : ''}Multi-select
            </button>
            <button
              data-testid="editor-filter-unreviewed"
              style={{
                ...styles.filterChip(filterUnreviewed),
                border: `1px solid ${filterUnreviewed ? '#b26a00' : '#ddd'}`,
                background: filterUnreviewed ? '#fff4e5' : '#fff',
                color: filterUnreviewed ? '#b26a00' : '#888',
              }}
              onClick={() => setFilterUnreviewed((v) => !v)}
              aria-pressed={filterUnreviewed}
              title="Show only unreviewed questions (e.g. freshly imported)"
            >
              {filterUnreviewed ? '✕ ' : ''}Unreviewed
              {unreviewedCount > 0 && (
                <span style={{
                  marginLeft: '4px',
                  padding: '0 5px',
                  borderRadius: '8px',
                  fontSize: '0.72rem',
                  fontWeight: '700',
                  background: filterUnreviewed ? '#b26a00' : '#e0e0e0',
                  color: filterUnreviewed ? '#fff' : '#666',
                }}>
                  {unreviewedCount}
                </span>
              )}
            </button>
            <button
              data-testid="editor-filter-uncategorized"
              style={{
                ...styles.filterChip(filterUncategorized),
                border: `1px solid ${filterUncategorized ? '#1565c0' : '#ddd'}`,
                background: filterUncategorized ? '#e3f2fd' : '#fff',
                color: filterUncategorized ? '#1565c0' : '#888',
              }}
              onClick={() => setFilterUncategorized((v) => !v)}
              aria-pressed={filterUncategorized}
              title="Show only questions with no category set"
            >
              {filterUncategorized ? '✕ ' : ''}No Category
              {uncategorizedCount > 0 && (
                <span style={{
                  marginLeft: '4px',
                  padding: '0 5px',
                  borderRadius: '8px',
                  fontSize: '0.72rem',
                  fontWeight: '700',
                  background: filterUncategorized ? '#1565c0' : '#e0e0e0',
                  color: filterUncategorized ? '#fff' : '#666',
                }}>
                  {uncategorizedCount}
                </span>
              )}
            </button>
          </div>
          {filterBatchIds.length > 0 && (
            <div
              data-testid="editor-batch-filter-chip"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                margin: '0 0 8px',
                padding: '8px 10px',
                borderRadius: '6px',
                border: '1px solid #2e7d32',
                background: '#e8f5e9',
                color: '#1b5e20',
                fontSize: '0.85rem',
              }}
            >
              <span>
                <strong>Viewing import batch</strong>
                {` · ${filteredQuestions.length} question${filteredQuestions.length !== 1 ? 's' : ''}`}
              </span>
              <button
                data-testid="editor-batch-filter-clear"
                onClick={() => setFilterBatchIds([])}
                style={{
                  border: '1px solid #2e7d32',
                  background: '#fff',
                  color: '#1b5e20',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                title="Show all questions again"
              >
                Clear
              </button>
            </div>
          )}
          <div style={styles.questionCount}>
            {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''}
            {filterMultiSelect && ' (multi-select only)'}
            {filterUncategorized && ' (no category)'}
            {filterUnreviewed && ' (unreviewed)'}
            {filterBatchIds.length > 0 && ' (recent import)'}
          </div>
          <ul style={styles.questionList} ref={sidebarListRef}>
            {filteredQuestions.map((q, i) => (
              <li
                key={q.id}
                data-testid="question-list-item"
                data-selected={q.id === selectedId ? 'true' : undefined}
                data-reference-id={q.referenceId}
                data-multiselect={q.multiSelect ? 'true' : undefined}
                style={{
                  ...styles.questionListItem,
                  ...(q.id === selectedId ? styles.questionListItemSelected : {}),
                }}
                onClick={() => handleSelectQuestion(q)}
              >
                {q.referenceId && (
                  <span style={styles.refIdChip}>{q.referenceId}</span>
                )}
                {q.multiSelect && (
                  <span
                    data-testid="multiselect-badge"
                    style={styles.multiSelectChip}
                    title="Multi-select question"
                  >
                    multi
                  </span>
                )}
                <span>
                  <strong>Q{i + 1}.</strong>{' '}
                  {q.question.length > 72 ? q.question.slice(0, 72) + '…' : q.question}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right — editor form */}
        <div style={styles.editorPane}>
          {/* Prev / Next navigation strip */}
          {filteredQuestions.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: '8px 12px',
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              fontSize: '0.85rem',
            }}>
              <button
                onClick={handlePrev}
                disabled={!hasPrev}
                style={{
                  padding: '4px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  background: hasPrev ? '#fff' : '#f5f5f5',
                  color: hasPrev ? '#333' : '#bbb',
                  cursor: hasPrev ? 'pointer' : 'default',
                  fontSize: '0.85rem',
                }}
              >← Prev</button>
              <span style={{ flex: 1, textAlign: 'center', color: '#666' }}>
                {selectedIndexInFiltered >= 0
                  ? `${selectedIndexInFiltered + 1} of ${filteredQuestions.length}`
                  : `${filteredQuestions.length} questions`}
              </span>
              <button
                onClick={handleNext}
                disabled={!hasNext}
                style={{
                  padding: '4px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  background: hasNext ? '#fff' : '#f5f5f5',
                  color: hasNext ? '#333' : '#bbb',
                  cursor: hasNext ? 'pointer' : 'default',
                  fontSize: '0.85rem',
                }}
              >Next →</button>
            </div>
          )}
          {!formData ? (
            <div style={styles.emptyState}>
              Select a question from the list to edit it, or click "+ New Question" to create one.
            </div>
          ) : (
            <>
              <h2 style={styles.sectionTitle}>
                {isCreating ? 'New Question' : 'Edit Question'}
              </h2>

              {/* Saved banner — shown after any successful save for the current question.
                   savedId is only set inside the try-block after the DB write confirms,
                   so this never flashes prematurely on a fire-and-forget. */}
              {savedId !== null && savedId === selectedId && !isCreating && (
                <div data-testid="editor-saved-banner" style={styles.savedBanner}>
                  ✓ Question saved successfully.
                </div>
              )}

              {/* Error banner */}
              {saveStatus === 'error' && saveError && (
                <div data-testid="editor-save-error" style={styles.errorBanner}>
                  ✗ {saveError}
                </div>
              )}

              {/* Reference ID — read-only */}
              {selectedReferenceId && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Reference ID</label>
                  <input
                    data-testid="editor-reference-id"
                    style={styles.refIdInput}
                    type="text"
                    value={selectedReferenceId}
                    readOnly
                    tabIndex={-1}
                    title="Reference IDs are system-generated and cannot be changed"
                  />
                </div>
              )}

              {/* Question text */}
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="editor-question-input">
                  Question Text
                </label>
                <textarea
                  id="editor-question-input"
                  data-testid="editor-question-input"
                  style={{ ...styles.textarea, minHeight: '80px' }}
                  value={formData.question}
                  onChange={(e) => handleFieldChange('question', e.target.value)}
                  rows={4}
                />
                {errors.question && (
                  <div data-testid="editor-error-question" style={styles.errorText}>
                    {errors.question}
                  </div>
                )}
              </div>

              {/* Answer choices — interactive (case) types get their own
                  editor; classic types keep the original list. */}
              {INTERACTIVE_TYPES.has(formData.type) && (
                <InteractiveAnswersEditor
                  formData={formData}
                  patch={(updater) => setFormData((prev) => updater(prev))}
                  errors={errors}
                />
              )}
              {!INTERACTIVE_TYPES.has(formData.type) && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Answer Choices</label>
                {formData.choices.map((choice, i) => (
                  <div key={choice.id} style={styles.choiceRow}>
                    <input
                      data-testid="editor-correct-checkbox"
                      type="checkbox"
                      id={`correct-${choice.id}`}
                      checked={formData.correctAnswers.includes(choice.id)}
                      onChange={() => handleCorrectToggle(choice.id)}
                      title="Mark as correct answer"
                    />
                    <label
                      htmlFor={`correct-${choice.id}`}
                      style={{ fontWeight: 'bold', minWidth: '24px', color: '#555' }}
                    >
                      {choice.id}.
                    </label>
                    <input
                      data-testid="editor-choice-input"
                      style={styles.choiceInput}
                      type="text"
                      value={choice.text}
                      onChange={(e) => handleChoiceText(i, e.target.value)}
                      placeholder={`Choice ${choice.id} text`}
                    />
                    {formData.choices.length > 1 && (
                      <button
                        style={styles.removeChoiceButton}
                        onClick={() => handleRemoveChoice(i)}
                        aria-label={`Remove choice ${choice.id}`}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  data-testid="editor-add-choice-button"
                  style={styles.addChoiceButton}
                  onClick={handleAddChoice}
                >
                  + Add Choice
                </button>
                {errors.choices && (
                  <div data-testid="editor-error-choices" style={styles.errorText}>
                    {errors.choices}
                  </div>
                )}
                {errors.correctAnswers && (
                  <div data-testid="editor-error-correct-answers" style={styles.errorText}>
                    {errors.correctAnswers}
                  </div>
                )}
              </div>
              )}

              {/* Explanation */}
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="editor-explanation-input">
                  Explanation
                </label>
                <textarea
                  id="editor-explanation-input"
                  data-testid="editor-explanation-input"
                  style={{ ...styles.textarea, minHeight: '60px' }}
                  value={formData.explanation}
                  onChange={(e) => handleFieldChange('explanation', e.target.value)}
                  rows={3}
                />
                {errors.explanation && (
                  <div data-testid="editor-error-explanation" style={styles.errorText}>
                    {errors.explanation}
                  </div>
                )}
              </div>

              {/* Category — PMP-only relabeled as "Lifecycle Phase" since the
                  PMP bank's category column maps to the 5 PMI process groups
                  (Initiating / Planning / Execution / Monitoring and
                  Controlling / Closing). Other certs keep the generic
                  "Category" label. */}
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="editor-category-select">
                  {selectedExam === 'pmp' ? 'Lifecycle Phase' : 'Category'}
                </label>
                {/* Legacy-value warning: shown when the stored value isn't in the
                    known list for this exam (e.g. imported from an old JSON file) */}
                {formData.category && !isKnownCategory(selectedExam, formData.category) && (
                  <div style={{
                    marginBottom: 4,
                    padding: '4px 8px',
                    background: '#fff8e1',
                    border: '1px solid #ffe082',
                    borderRadius: 4,
                    fontSize: 12,
                    color: '#795548',
                  }}>
                    ⚠ Unmapped category: <strong>{formData.category}</strong> — select a standard category below or leave blank.
                  </div>
                )}
                <select
                  id="editor-category-select"
                  data-testid="editor-category-input"
                  style={{ ...styles.input, cursor: 'pointer' }}
                  value={isKnownCategory(selectedExam, formData.category) ? (formData.category || '') : ''}
                  onChange={(e) => handleFieldChange('category', e.target.value)}
                >
                  <option value="">Select category…</option>
                  {(EXAM_CATEGORIES[selectedExam] || []).map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* ── PMP-only taxonomy dimensions (migration 008) ──────────
                  Rendered only for the PMP exam.  Both are OPTIONAL:
                  '' (blank option) represents "not set" and is stored as
                  NULL in the database.  CPMAI editing is unchanged. */}
              {selectedExam === 'pmp' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="editor-performance-domain-select">
                      Performance Domain
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                        (PMP, optional)
                      </span>
                    </label>
                    <select
                      id="editor-performance-domain-select"
                      data-testid="editor-performance-domain-select"
                      style={{ ...styles.input, cursor: 'pointer' }}
                      value={formData.performanceDomain || ''}
                      onChange={(e) => handleFieldChange('performanceDomain', e.target.value)}
                    >
                      <option value="">(none)</option>
                      {PMP_PERFORMANCE_DOMAINS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="editor-delivery-context-select">
                      Delivery Context
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                        (PMP, optional)
                      </span>
                    </label>
                    <select
                      id="editor-delivery-context-select"
                      data-testid="editor-delivery-context-select"
                      style={{ ...styles.input, cursor: 'pointer' }}
                      value={formData.deliveryContext || ''}
                      onChange={(e) => handleFieldChange('deliveryContext', e.target.value)}
                    >
                      <option value="">(none)</option>
                      {PMP_DELIVERY_CONTEXTS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* ── PMI-ACP ECO metadata (migrations 032 + 033) ─────────
                  Rendered only for the PMI-ACP exam.  All fields OPTIONAL.
                  `ecoDomain` is NOT shown here -- it's synced with the
                  `category` dropdown above (which already lists the four
                  ECO domains). */}
              {selectedExam === 'pmi-acp' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="editor-eco-task-input">
                      ECO Task
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                        (PMI-ACP, optional, free-form)
                      </span>
                    </label>
                    <input
                      id="editor-eco-task-input"
                      data-testid="editor-eco-task-input"
                      type="text"
                      style={styles.input}
                      value={formData.ecoTask || ''}
                      onChange={(e) => handleFieldChange('ecoTask', e.target.value)}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="editor-practice-area-select">
                      Practice Area
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                        (PMI-ACP, optional)
                      </span>
                    </label>
                    <select
                      id="editor-practice-area-select"
                      data-testid="editor-practice-area-select"
                      style={{ ...styles.input, cursor: 'pointer' }}
                      value={formData.practiceArea || ''}
                      onChange={(e) => handleFieldChange('practiceArea', e.target.value)}
                    >
                      <option value="">(none)</option>
                      {PMI_ACP_PRACTICE_AREAS.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="editor-knowledge-area-input">
                      Knowledge Area
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                        (PMI-ACP, optional, free-form)
                      </span>
                    </label>
                    <input
                      id="editor-knowledge-area-input"
                      data-testid="editor-knowledge-area-input"
                      type="text"
                      style={styles.input}
                      value={formData.knowledgeArea || ''}
                      onChange={(e) => handleFieldChange('knowledgeArea', e.target.value)}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="editor-artifact-input">
                      Artifact
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                        (PMI-ACP, optional, free-form)
                      </span>
                    </label>
                    <input
                      id="editor-artifact-input"
                      data-testid="editor-artifact-input"
                      type="text"
                      style={styles.input}
                      value={formData.artifact || ''}
                      onChange={(e) => handleFieldChange('artifact', e.target.value)}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="editor-cognitive-level-select">
                      Cognitive Level
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                        (PMI-ACP, optional)
                      </span>
                    </label>
                    <select
                      id="editor-cognitive-level-select"
                      data-testid="editor-cognitive-level-select"
                      style={{ ...styles.input, cursor: 'pointer' }}
                      value={formData.cognitiveLevel || ''}
                      onChange={(e) => handleFieldChange('cognitiveLevel', e.target.value)}
                    >
                      <option value="">(none)</option>
                      {COGNITIVE_LEVELS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="editor-difficulty-select">
                      Difficulty
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                        (PMI-ACP, optional)
                      </span>
                    </label>
                    <select
                      id="editor-difficulty-select"
                      data-testid="editor-difficulty-select"
                      style={{ ...styles.input, cursor: 'pointer' }}
                      value={formData.difficulty || ''}
                      onChange={(e) => handleFieldChange('difficulty', e.target.value)}
                    >
                      <option value="">(none)</option>
                      {DIFFICULTIES.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="editor-exam-weight-select">
                      Exam Weight
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                        (PMI-ACP, optional)
                      </span>
                    </label>
                    <select
                      id="editor-exam-weight-select"
                      data-testid="editor-exam-weight-select"
                      style={{ ...styles.input, cursor: 'pointer' }}
                      value={formData.examWeight || ''}
                      onChange={(e) => handleFieldChange('examWeight', e.target.value)}
                    >
                      <option value="">(none)</option>
                      {EXAM_WEIGHTS.map((w) => (
                        <option key={w} value={w}>{w}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label
                      style={{ ...styles.label, display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                      htmlFor="editor-scenario-based-checkbox"
                    >
                      <input
                        id="editor-scenario-based-checkbox"
                        data-testid="editor-scenario-based-checkbox"
                        type="checkbox"
                        checked={formData.scenarioBased === true}
                        onChange={(e) => handleFieldChange('scenarioBased', e.target.checked)}
                        style={{ cursor: 'pointer', margin: 0 }}
                      />
                      <span>Scenario-Based</span>
                    </label>
                    <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '4px', marginLeft: '24px' }}>
                      Question presents a real-world agile scenario rather than a definitional / recall prompt.
                    </div>
                  </div>
                </>
              )}

              {/* Secondary dimension (config-driven; PMOCP focus_area today).
                  Rendered for any cert with SECONDARY_DIMENSION_VALUES. */}
              {/* ECO domain — REQUIRED for certs that declare one (CPMAI today).
                  A separate control from Focus Area on purpose: this writes
                  questions.eco_domain, Focus Area writes questions.focus_area,
                  and they are different dimensions belonging to different certs.
                  The five values are how PMI SCORES the exam; questions.category
                  holds the six methodology phases the course TEACHES. Do not
                  derive one from the other. */}
              {isEcoDomainRequired(selectedExam) && (
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="editor-eco-domain-select">
                    ECO Domain
                    <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                      ({selectedExam.toUpperCase()}, required)
                    </span>
                  </label>
                  <select
                    id="editor-eco-domain-select"
                    data-testid="editor-eco-domain-select"
                    style={{ ...styles.input, cursor: 'pointer' }}
                    value={formData.ecoDomain || ''}
                    onChange={(e) => handleFieldChange('ecoDomain', e.target.value)}
                  >
                    <option value="">(select an ECO domain)</option>
                    {CPMAI_ECO_DOMAINS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  {errors.ecoDomain && (
                    <div style={styles.errorText} data-testid="editor-error-eco-domain">
                      {errors.ecoDomain}
                    </div>
                  )}
                </div>
              )}

              {Array.isArray(SECONDARY_DIMENSION_VALUES[selectedExam]) && (
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="editor-focus-area-select">
                    Focus Area
                    <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                      ({selectedExam.toUpperCase()}, optional)
                    </span>
                  </label>
                  <select
                    id="editor-focus-area-select"
                    data-testid="editor-focus-area-select"
                    style={{ ...styles.input, cursor: 'pointer' }}
                    value={formData.focusArea || ''}
                    onChange={(e) => handleFieldChange('focusArea', e.target.value)}
                  >
                    <option value="">(none)</option>
                    {SECONDARY_DIMENSION_VALUES[selectedExam].map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* ── Experimental flag (Phase 2, migration 012) ─────────
                  Available for every exam.  When checked, the question is
                  shown to learners but excluded from scoring; analytics still
                  capture full attempt rows. */}
              <div style={styles.formGroup}>
                <label
                  style={{ ...styles.label, display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                  htmlFor="editor-is-experimental-checkbox"
                >
                  <input
                    id="editor-is-experimental-checkbox"
                    data-testid="editor-is-experimental-checkbox"
                    type="checkbox"
                    checked={formData.isExperimental === true}
                    onChange={(e) => handleFieldChange('isExperimental', e.target.checked)}
                    style={{ cursor: 'pointer', margin: 0 }}
                  />
                  <span>Experimental (unscored)</span>
                </label>
                <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '4px', marginLeft: '24px' }}>
                  Shown to learners alongside scored questions but excluded from score percentage and pass/fail.
                  Still captured in question_attempts analytics.
                </div>
              </div>

              {/* Source */}
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="editor-source-input">
                  Source / Reference
                </label>
                <input
                  id="editor-source-input"
                  data-testid="editor-source-input"
                  style={styles.input}
                  type="text"
                  value={formData.source}
                  onChange={(e) => handleFieldChange('source', e.target.value)}
                />
              </div>

              {/* Confidence */}
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="editor-confidence-select">
                  Confidence
                </label>
                <select
                  id="editor-confidence-select"
                  data-testid="editor-confidence-select"
                  style={styles.select}
                  value={formData.confidence}
                  onChange={(e) => handleFieldChange('confidence', e.target.value)}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              {/* Phase 3 — Question type selector (Multiple Choice / Multiple Select / Visual Interpretation) */}
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="editor-type-select">Question Type</label>
                {INTERACTIVE_TYPES.has(formData.type) && (
                  <div data-testid="editor-type-interactive" style={{ ...styles.input, background: '#f4f4f4', color: '#555' }}>
                    {interactiveTypeLabel(formData.type)}
                    <span style={{ display: 'block', fontSize: '0.72rem', color: '#888' }}>
                      Case question types can't be converted to classic types.
                    </span>
                  </div>
                )}
                {!INTERACTIVE_TYPES.has(formData.type) && (
                <select
                  id="editor-type-select"
                  data-testid="editor-type-select"
                  style={{ ...styles.input, cursor: 'pointer' }}
                  value={formData.type ?? (formData.multiSelect ? 'multiple_select' : 'multiple_choice')}
                  onChange={(e) => {
                    const t = e.target.value;
                    // Sync multiSelect to keep the rest of the form coherent.
                    handleFieldChange('type', t);
                    handleFieldChange('multiSelect', t === 'multiple_select');
                  }}
                >
                  <option value="multiple_choice">Multiple Choice (single answer)</option>
                  <option value="multiple_select">Multiple Select (multiple correct answers)</option>
                  <option value="visual_interpretation">Visual Interpretation (image exhibit)</option>
                </select>
                )}
              </div>

              {/* Image fields — only for visual_interpretation */}
              {(formData.type === 'visual_interpretation') && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="editor-image-src-input">
                      Image URL or Path
                      <span style={{ fontWeight: 400, color: '#c62828', marginLeft: 6 }}>*</span>
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        id="editor-image-src-input"
                        data-testid="editor-image-src-input"
                        style={{ ...styles.input, flex: 1 }}
                        type="text"
                        placeholder="/images/questions/…  or  https://…  or click Upload →"
                        value={formData.image?.src ?? ''}
                        onChange={(e) => handleFieldChange('image', {
                          ...(formData.image ?? {}),
                          src: e.target.value,
                        })}
                      />
                      <button
                        type="button"
                        data-testid="editor-image-upload-button"
                        onClick={() => imageFileInputRef.current?.click()}
                        disabled={imageUploadStatus === 'uploading'}
                        style={{
                          padding: '8px 16px',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#1565c0',
                          background: '#fff',
                          border: '1px solid #1565c0',
                          borderRadius: '4px',
                          cursor: imageUploadStatus === 'uploading' ? 'wait' : 'pointer',
                          whiteSpace: 'nowrap',
                          opacity: imageUploadStatus === 'uploading' ? 0.7 : 1,
                        }}
                      >
                        {imageUploadStatus === 'uploading' ? 'Uploading…' : '⬆ Upload'}
                      </button>
                      {/* Hidden file picker — triggered by the Upload button above. */}
                      <input
                        ref={imageFileInputRef}
                        data-testid="editor-image-file-input"
                        type="file"
                        accept={IMAGE_UPLOAD_MIME_TYPES.join(',')}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0];
                          // Reset the input so re-selecting the same filename
                          // re-fires onChange (browsers de-dupe by default).
                          e.target.value = '';
                          if (file) handleImageUpload(file);
                        }}
                      />
                    </div>
                    {errors.imageSrc && (
                      <div data-testid="editor-error-image-src" style={styles.errorText}>
                        {errors.imageSrc}
                      </div>
                    )}
                    {imageUploadError && (
                      <div data-testid="editor-image-upload-error" style={styles.errorText}>
                        {imageUploadError}
                      </div>
                    )}
                    <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '4px' }}>
                      Upload PNG / JPG / WebP / SVG up to {Math.round(IMAGE_UPLOAD_MAX_BYTES / 1024 / 1024)} MB,
                      or paste an external URL above.
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="editor-image-alt-input">
                      Image Alt Text
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                        (recommended for accessibility)
                      </span>
                    </label>
                    <input
                      id="editor-image-alt-input"
                      data-testid="editor-image-alt-input"
                      style={styles.input}
                      type="text"
                      placeholder="Burndown chart showing scope creep mid-sprint…"
                      value={formData.image?.alt ?? ''}
                      onChange={(e) => handleFieldChange('image', {
                        ...(formData.image ?? {}),
                        alt: e.target.value,
                      })}
                    />
                  </div>
                  {/* Preview — only when src is set so admins can verify the
                      image rendered correctly before saving. */}
                  {formData.image?.src && (
                    <div style={styles.formGroup}>
                      <div
                        data-testid="editor-image-preview-wrapper"
                        style={{
                          padding: '12px',
                          background: '#f5f5f5',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          textAlign: 'center',
                        }}
                      >
                        <img
                          data-testid="editor-image-preview"
                          src={formData.image.src}
                          alt={formData.image.alt || 'Question image preview'}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '240px',
                            height: 'auto',
                            objectFit: 'contain',
                            display: 'block',
                            margin: '0 auto',
                          }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Multi-select — disabled when visual_interpretation is selected
                  (multi-select visual exhibits are out of scope for Phase 3). */}
              <div style={styles.formGroup}>
                <label style={styles.checkboxRow}>
                  <input
                    data-testid="editor-multiselect-checkbox"
                    type="checkbox"
                    checked={formData.multiSelect}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      handleFieldChange('multiSelect', checked);
                      // Keep the type dropdown in sync, but never override visual_interpretation.
                      if (formData.type !== 'visual_interpretation') {
                        handleFieldChange('type', checked ? 'multiple_select' : 'multiple_choice');
                      }
                    }}
                    disabled={formData.type === 'visual_interpretation'}
                  />
                  Multi-select question (allow choosing multiple correct answers)
                </label>
              </div>

              {/* Change note */}
              {!isCreating && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Change Note (optional)</label>
                  <input
                    data-testid="editor-change-note-input"
                    style={styles.input}
                    type="text"
                    placeholder="Briefly describe what you changed…"
                    value={formData.changeNote || ''}
                    onChange={(e) => handleFieldChange('changeNote', e.target.value)}
                  />
                </div>
              )}

              {/* ── Question Status panel ────────────────────────────── */}
              {!isCreating && selectedId && (() => {
                const selectedQ   = (questionStores[selectedExam] ?? []).find((q) => q.id === selectedId);
                const qStatus     = selectedQ?.status || 'active';
                const statusCfg   = QUESTION_STATUS_COLORS[qStatus] || QUESTION_STATUS_COLORS.active;
                const isLifeSaving = lifecycleStatus === 'saving';

                // All OTHER pending questions in this exam (sorted by id) for the Next button.
                // Skip reported/removed questions so Next Pending advances past flagged items.
                const pendingOthers = (questionStores[selectedExam] ?? [])
                  .filter((q) => q.status === 'new' && q.id !== selectedId
                    && q.reviewStatus !== 'reported' && q.reviewStatus !== 'removed')
                  .sort((a, b) => String(a.id).localeCompare(String(b.id)));
                const nextPending = pendingOthers[0] || null;

                return (
                  <div style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    marginBottom: '12px',
                    background: '#fafafa',
                  }}>
                    <div style={{
                      fontSize: '0.75rem', fontWeight: '700', color: '#888',
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      marginBottom: '10px',
                    }}>
                      Question Status
                    </div>

                    {/* Status badge + actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.75rem', fontWeight: '700',
                        padding: '3px 12px', borderRadius: '12px',
                        background: statusCfg.bg, color: statusCfg.color,
                        border: `1px solid ${statusCfg.border}`,
                      }}>
                        {QUESTION_STATUS_LABELS[qStatus] || qStatus}
                      </span>

                      {qStatus === 'new' && (
                        <button
                          data-testid="editor-approve-button"
                          disabled={isLifeSaving}
                          style={{
                            padding: '4px 16px', fontSize: '0.82rem', fontWeight: '700',
                            background: '#2e7d32', color: '#fff', border: 'none',
                            borderRadius: '6px', cursor: isLifeSaving ? 'default' : 'pointer',
                            opacity: isLifeSaving ? 0.7 : 1,
                          }}
                          onClick={() => handleLifecycleUpdate({
                            status: 'active',
                            reviewStatus: 'reviewed',
                            changeNote: 'Approved via Question Editor',
                          })}
                        >
                          {isLifeSaving ? 'Saving…' : '✓ Approve'}
                        </button>
                      )}

                      {nextPending && (
                        <button
                          data-testid="editor-next-pending-button"
                          disabled={isLifeSaving}
                          style={{
                            padding: '4px 14px', fontSize: '0.82rem', fontWeight: '600',
                            background: '#fff', color: '#1565c0',
                            border: '1px solid #90caf9', borderRadius: '6px',
                            cursor: isLifeSaving ? 'default' : 'pointer',
                            opacity: isLifeSaving ? 0.7 : 1,
                          }}
                          onClick={() => handleSelectQuestion(nextPending)}
                        >
                          Next Pending →
                          <span style={{ marginLeft: '6px', fontSize: '0.72rem', color: '#888' }}>
                            ({pendingOthers.length})
                          </span>
                        </button>
                      )}
                    </div>

                    {/* Batch / generator metadata */}
                    {(selectedQ?.sourceBatchId || selectedQ?.sourceGenerator) && (
                      <div style={{ marginTop: '8px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {selectedQ.sourceBatchId && (
                          <span style={{ fontSize: '0.72rem', color: '#aaa', fontFamily: 'monospace' }}>
                            batch: {selectedQ.sourceBatchId}
                          </span>
                        )}
                        {selectedQ.sourceGenerator && (
                          <span style={{ fontSize: '0.72rem', color: '#aaa' }}>
                            via {selectedQ.sourceGenerator}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Lifecycle feedback */}
                    {lifecycleStatus === 'saved' && (
                      <div style={{ marginTop: '8px', fontSize: '0.78rem', color: '#2e7d32' }}>
                        ✓ Status updated
                      </div>
                    )}
                    {lifecycleStatus === 'error' && lifecycleError && (
                      <div style={{ marginTop: '8px', fontSize: '0.78rem', color: '#c62828' }}>
                        ✗ {lifecycleError}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── Review Status panel ──────────────────────────────── */}
              {!isCreating && selectedId && (() => {
                const selectedQ   = (questionStores[selectedExam] ?? []).find((q) => q.id === selectedId);
                const qReview     = selectedQ?.reviewStatus || 'unreviewed';
                const reviewCfg   = REVIEW_STATUS_COLORS[qReview] || REVIEW_STATUS_COLORS.unreviewed;
                const isLifeSaving = lifecycleStatus === 'saving';
                const draftReview  = reviewStatusDraft !== null ? reviewStatusDraft : qReview;

                return (
                  <div style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    marginBottom: '20px',
                    background: '#fafafa',
                  }}>
                    <div style={{
                      fontSize: '0.75rem', fontWeight: '700', color: '#888',
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      marginBottom: '10px',
                    }}>
                      Review Status
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.75rem', fontWeight: '700',
                        padding: '3px 12px', borderRadius: '12px',
                        background: reviewCfg.bg, color: reviewCfg.color,
                        border: `1px solid ${reviewCfg.border}`,
                      }}>
                        {REVIEW_STATUS_LABELS[qReview] || qReview}
                      </span>
                      <select
                        data-testid="editor-review-status-select"
                        disabled={isLifeSaving}
                        style={{
                          padding: '4px 8px', fontSize: '0.8rem',
                          border: '1px solid #ccc', borderRadius: '6px',
                          background: '#fff', cursor: 'pointer',
                        }}
                        value={draftReview}
                        onChange={(e) => setReviewStatusDraft(e.target.value)}
                      >
                        {REVIEW_STATUSES.filter((rs) => rs !== 'removed').map((rs) => (
                          <option key={rs} value={rs}>{REVIEW_STATUS_LABELS[rs]}</option>
                        ))}
                      </select>
                      {draftReview !== qReview && (
                        <button
                          data-testid="editor-save-review-status-button"
                          disabled={isLifeSaving}
                          style={{
                            padding: '4px 14px', fontSize: '0.8rem', fontWeight: '700',
                            background: '#1565c0', color: '#fff', border: 'none',
                            borderRadius: '6px', cursor: isLifeSaving ? 'default' : 'pointer',
                            opacity: isLifeSaving ? 0.7 : 1,
                          }}
                          onClick={() => handleLifecycleUpdate({
                            reviewStatus: draftReview,
                            changeNote: `Review status set to ${draftReview}`,
                          })}
                        >
                          {isLifeSaving ? 'Saving…' : 'Save'}
                        </button>
                      )}

                      {/* Remove (quarantine) button — retires + marks removed */}
                      <button
                        data-testid="editor-remove-button"
                        disabled={isLifeSaving}
                        style={{
                          marginLeft: 'auto',
                          padding: '4px 14px', fontSize: '0.8rem', fontWeight: '700',
                          background: '#fff', color: '#c62828',
                          border: '1px solid #ef9a9a', borderRadius: '6px',
                          cursor: isLifeSaving ? 'default' : 'pointer',
                          opacity: isLifeSaving ? 0.7 : 1,
                        }}
                        onClick={() => {
                          if (window.confirm('Remove this question? It will be quarantined and hidden from the editor.')) {
                            handleLifecycleUpdate({
                              status: 'retired',
                              reviewStatus: 'removed',
                              changeNote: 'Quarantined via Remove button',
                            });
                          }
                        }}
                      >
                        {isLifeSaving ? 'Removing…' : '✗ Remove'}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Save / Reset / Retire */}
              <div style={styles.actions}>
                <button
                  data-testid="editor-save-button"
                  style={isSaving ? styles.saveButtonSaving : styles.saveButton}
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving
                    ? 'Saving…'
                    : isCreating
                    ? 'Add Question'
                    : 'Save Changes'}
                </button>
                <button
                  data-testid="editor-cancel-button"
                  style={styles.cancelButton}
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  {isCreating ? 'Cancel' : 'Reset'}
                </button>

                {/* Phase 3.6 — Preview as Learner + Copy preview link */}
                <button
                  type="button"
                  data-testid="editor-preview-button"
                  style={{
                    padding: '10px 18px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: '#1565c0',
                    background: '#fff',
                    border: '1px solid #1565c0',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                  onClick={() => setPreviewOpen(true)}
                >
                  👁 Preview as Learner
                </button>
                {!isCreating && selectedId && (
                  <button
                    type="button"
                    data-testid="editor-copy-preview-link-button"
                    style={{
                      padding: '10px 14px',
                      fontSize: '0.85rem',
                      color: copyLinkStatus === 'copied' ? '#2e7d32' : '#555',
                      background: '#fff',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                    onClick={handleCopyPreviewLink}
                    title="Copy a shareable preview link to the clipboard"
                  >
                    {copyLinkStatus === 'copied' ? '✓ Copied!' :
                     copyLinkStatus === 'error'  ? 'Copy failed' :
                                                   '🔗 Copy preview link'}
                  </button>
                )}

                {/* Retire — only in edit mode */}
                {!isCreating && (
                  confirmRetire ? (
                    <div data-testid="editor-retire-confirm" style={styles.retireConfirmRow}>
                      <span>Retire this question? It will be excluded from exams.</span>
                      <button
                        data-testid="editor-retire-confirm-button"
                        style={styles.retireConfirmButton}
                        onClick={handleRetire}
                        disabled={isSaving}
                      >
                        Yes, Retire
                      </button>
                      <button
                        style={styles.cancelButton}
                        onClick={() => setConfirmRetire(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid="editor-retire-button"
                      style={styles.retireButton}
                      onClick={() => setConfirmRetire(true)}
                      disabled={isSaving}
                    >
                      Retire
                    </button>
                  )
                )}
              </div>

              {/* ── Version History ──────────────────────────────────────── */}
              {!isCreating && selectedId && (
                <div style={styles.historySection}>
                  <button
                    data-testid="editor-history-toggle"
                    style={styles.historyToggleButton}
                    onClick={handleHistoryToggle}
                  >
                    {showHistory ? '▲ Hide Version History' : '▼ View Version History'}
                  </button>

                  {showHistory && (
                    <div
                      data-testid="editor-history-panel"
                      style={styles.historyPanel}
                    >
                      {questionHistory === null ? (
                        <div style={styles.historyMessage}>Loading history…</div>
                      ) : questionHistory.length === 0 ? (
                        <div style={styles.historyMessage}>No history available for this question.</div>
                      ) : (
                        <table style={styles.historyTable}>
                          <thead>
                            <tr>
                              <th style={styles.historyTh}>Ver</th>
                              <th style={styles.historyTh}>Type</th>
                              <th style={styles.historyTh}>Date</th>
                              <th style={styles.historyTh}>Changed By</th>
                              <th style={styles.historyTh}>Note</th>
                              <th style={styles.historyTh}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {questionHistory.map((entry) => (
                              <tr key={entry.id} data-testid="editor-history-row">
                                <td style={styles.historyTd}>v{entry.versionNo}</td>
                                <td style={styles.historyTd}>
                                  <span style={styles.historyChangeTypeBadge(entry.changeType)}>
                                    {entry.changeType}
                                  </span>
                                </td>
                                <td style={styles.historyTd}>
                                  {entry.changedAt
                                    ? new Date(entry.changedAt).toLocaleString()
                                    : '—'}
                                </td>
                                <td
                                  style={{
                                    ...styles.historyTd,
                                    fontFamily: 'system-ui, sans-serif',
                                    fontSize: '0.85rem',
                                    color: entry.changedBy && entry.changedBy !== 'editor'
                                      ? '#1565c0'
                                      : '#888',
                                    fontStyle: entry.changedBy === 'editor' ? 'italic' : 'normal',
                                    maxWidth: '220px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={entry.changedBy || '(unknown)'}
                                  data-testid="editor-history-changed-by"
                                >
                                  {entry.changedBy || '—'}
                                </td>
                                <td style={styles.historyTd}>
                                  {entry.changeNote || '—'}
                                </td>
                                <td style={styles.historyTd}>
                                  <button
                                    data-testid="editor-restore-button"
                                    style={styles.restoreButton}
                                    onClick={() => handleRestore(entry)}
                                    disabled={isSaving}
                                    title={`Restore question to state after version ${entry.versionNo}`}
                                  >
                                    Restore
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
          {previewOpen && (
        <QuestionPreviewModal
          question={buildPreviewQuestion()}
          onClose={() => setPreviewOpen(false)}
        />
      )}
</div>
  );
}
